import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { PanelRecord } from './SidePanelContext';
import { makeKey } from './SidePanelContext';
import { useTransientOverlay } from '../../utils/transientOverlays';

interface BrowserTabsProps {
  records: PanelRecord[];
  activeKey: string | null;
  onPick: (key: string) => void;
  onClose: (key: string) => void;
  onReorder: (fromIdx: number, toIdx: number) => void;
  onCloseAll?: () => void;
  onCloseOthers?: (key: string) => void;
  onCloseToRight?: (key: string) => void;
  /** Copy a shareable link to this tab's record. */
  onCopyLink?: (key: string) => void;
}

function tabLabel(label: string): string {
  if (!label) return '';
  const words = label.replace(/,/g, '').split(/\s+/).filter(Boolean);
  return words.slice(0, 3).join(' ');
}

interface ContextMenuState {
  x: number;
  y: number;
  key: string;
  idx: number;
}

function BrowserTabs({
  records, activeKey, onPick, onClose, onReorder,
  onCloseAll, onCloseOthers, onCloseToRight, onCopyLink,
}: BrowserTabsProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleDragStart = useCallback((e: React.DragEvent, i: number) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(i)); } catch { e.preventDefault(); }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, i: number) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    setOverIdx(null);
    if (from === null || from === i) return;
    onReorder(from, i);
  }, [dragIdx, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setOverIdx(null);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent, key: string, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, key, idx });
  }, []);

  useTransientOverlay(!!menu);

  // Close menu on click outside or Escape
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const hasTabsToRight = menu ? menu.idx < records.length - 1 : false;

  return (
    <>
      <div className="sky-browser-tabs">
        {records.map((r, i) => {
          const key = makeKey(r.table, r.id);
          const active = key === activeKey;
          const isDragging = dragIdx === i;
          const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;

          return (
            <div
              key={key}
              className={`sky-browser-tab ${active ? 'sky-browser-tab--active' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
              onDragLeave={() => setOverIdx(prev => prev === i ? null : prev)}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              onClick={() => onPick(key)}
              onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); onClose(key); } }}
              onContextMenu={(e) => handleContextMenu(e, key, i)}
              style={{
                opacity: isDragging ? 0.4 : 1,
                boxShadow: isOver ? 'inset 2px 0 0 0 var(--accent)' : 'none',
              }}
            >
              <span className="sky-browser-tab-icon">
                {r.tableName.charAt(0).toUpperCase()}
              </span>
              <span className="sky-browser-tab-label">
                {tabLabel(r.label)}
              </span>
              <span
                className="sky-browser-tab-close"
                onClick={(e) => { e.stopPropagation(); onClose(key); }}
              >
                &times;
              </span>
              {!active && i < records.length - 1 && records[i + 1] && makeKey(records[i + 1].table, records[i + 1].id) !== activeKey && (
                <span className="sky-browser-tab-divider" />
              )}
            </div>
          );
        })}
      </div>

      {/* Context menu */}
      {menu && (
        <div
          ref={menuRef}
          className="sky-tab-ctx-menu"
          style={{ top: menu.y, left: menu.x }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {onCopyLink && (
            <>
              <button
                className="sky-tab-ctx-item"
                onClick={() => { onCopyLink(menu.key); setMenu(null); }}
              >
                Copy link
              </button>
              <div className="sky-tab-ctx-sep" />
            </>
          )}
          <button
            className="sky-tab-ctx-item"
            onClick={() => { onClose(menu.key); setMenu(null); }}
          >
            Close
          </button>
          {onCloseOthers && records.length > 1 && (
            <button
              className="sky-tab-ctx-item"
              onClick={() => { onCloseOthers(menu.key); setMenu(null); }}
            >
              Close others
            </button>
          )}
          {onCloseToRight && hasTabsToRight && (
            <button
              className="sky-tab-ctx-item"
              onClick={() => { onCloseToRight(menu.key); setMenu(null); }}
            >
              Close to the right
            </button>
          )}
          {onCloseAll && (
            <>
              <div className="sky-tab-ctx-sep" />
              <button
                className="sky-tab-ctx-item sky-tab-ctx-item--danger"
                onClick={() => { onCloseAll(); setMenu(null); }}
              >
                Close all
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default React.memo(BrowserTabs);
